import { inject, Injectable } from '@angular/core';
import { mapAgentGroupingTypeToProfileType, ProfileType } from '@rma/generic/domain/types';
import { getQueryStringParam } from '@rma/generic/ng/util';
import { ContextStorageService } from './context-storage.service';
import { CurrentUser } from './domain/current-user.models';
import { SelectedContext } from './domain/profile-context.models';

/**
 * InitialContextService
 *
 * Determines and provides the initial profile context (agent or agency) for the current user.
 *
 * - Exposes `getInitialContext(user)`, which returns the initial {@link SelectedContext} for the given user, or `undefined` if not applicable.
 *   - Checks the query string, then local storage, and finally falls back to a default context.
 * - Exposes `getDefaultContext(user)`, which returns the default context for the user (agent if present, otherwise first agency).
 *
 * @remarks
 * - I extracted this from the ContextSelectorService so I can test it easily
 */
@Injectable({
  providedIn: 'root',
})
export class InitialContextService {
  private readonly contextStorage = inject(ContextStorageService);

  public getInitialContext(user: CurrentUser | null): SelectedContext | undefined {
    if (user === null || user.userType === 'Consumer') {
      return undefined;
    }

    const queryContext = this.getContextFromQueryString();
    if (queryContext) {
      return queryContext;
    }

    const localStorageContext = this.getContextFromLocalStorage();
    if (localStorageContext) {
      return localStorageContext;
    }

    return this.getDefaultContext(user);
  }

  public getDefaultContext(user: CurrentUser): SelectedContext | undefined {
    if (user.userType === 'Consumer') {
      return undefined;
    } else if (user.agent) {
      return this.createContext('Individual', user.agent.agentCode);
    } else if (user.agencies && user.agencies.length > 0) {
      const agency = user.agencies[0];
      const profileType = mapAgentGroupingTypeToProfileType(agency.agentGroupingType);
      return this.createContext(profileType, agency.agencyCode);
    }

    return undefined;
  }

  private getContextFromLocalStorage(): SelectedContext | undefined {
    const context = this.contextStorage.getContext();

    if (context) {
      return this.createContext(context.profileType, context.profileCode);
    }

    return undefined;
  }

  private getContextFromQueryString(): SelectedContext | undefined {
    const queryContext = getQueryStringParam('context');

    if (queryContext) {
      const [profileType, profileCode] = queryContext.split(':');

      if (profileCode && profileType) {
        return this.createContext(profileType as ProfileType, profileCode);
      }
    }
    return undefined;
  }

  private createContext(profileType: ProfileType, profileCode: string): SelectedContext {
    return {
      profileType,
      profileCode,
    };
  }
}
